import React, { useState } from "react";
import {
  Box,
  Button,
  Container,
  Drawer,
  IconButton,
  Paper,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CloseIcon from "@mui/icons-material/Close";
import MapOutlined from "@mui/icons-material/MapOutlined";
import { useLocation } from "react-router-dom";
import { useActiveJourney } from "../../contexts/ActiveJourneyContext";
import GuidedJourneyPanel from "./GuidedJourneyPanel";

const JourneyCompanionBar: React.FC = () => {
  const theme = useTheme();
  const isLarge = useMediaQuery(theme.breakpoints.up("lg"));
  const location = useLocation();
  const {
    journey,
    hasActiveJourney,
    currentStepIndex,
    completedStepIds,
    continueJourney,
    resetJourney,
    executeStep,
  } = useActiveJourney();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [confirmDismiss, setConfirmDismiss] = useState(false);

  if (!hasActiveJourney || !journey || location.pathname === "/") {
    return null;
  }

  const totalSteps = journey.steps.length;
  const activeStep = journey.steps[currentStepIndex];
  const summary =
    journey.intentSummary.length > (isLarge ? 120 : 72)
      ? `${journey.intentSummary.slice(0, isLarge ? 120 : 72)}…`
      : journey.intentSummary;

  const handleDismiss = () => {
    if (!confirmDismiss) {
      setConfirmDismiss(true);
      return;
    }
    resetJourney();
    setConfirmDismiss(false);
  };

  return (
    <>
      <Paper
        elevation={6}
        sx={{
          position: "sticky",
          bottom: 0,
          zIndex: (t) => t.zIndex.appBar - 1,
          borderTop: "1px solid",
          borderColor: "divider",
          borderRadius: 0,
          py: 1.5,
          mt: "auto",
        }}
      >
        <Container maxWidth="xl">
          <Stack
            direction={{ xs: "column", md: "row" }}
            alignItems={{ xs: "stretch", md: "center" }}
            justifyContent="space-between"
            spacing={1.5}
          >
            <Stack direction="row" alignItems="flex-start" spacing={1.5} sx={{ minWidth: 0, flex: 1 }}>
              <MapOutlined color="primary" sx={{ mt: 0.25, flexShrink: 0 }} />
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="subtitle2" fontWeight={600}>
                  Your journey
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  noWrap={!isLarge}
                  sx={
                    isLarge
                      ? {
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                        }
                      : undefined
                  }
                >
                  {summary}
                </Typography>
                {activeStep && (
                  <Typography variant="caption" color="text.secondary">
                    Step {currentStepIndex + 1} of {totalSteps}: {activeStep.title}
                  </Typography>
                )}
              </Box>
            </Stack>

            <Stack direction="row" spacing={1} flexShrink={0} justifyContent="flex-end">
              <Button
                size="small"
                variant="outlined"
                onClick={() => setDrawerOpen(true)}
              >
                View journey
              </Button>
              <Button
                size="small"
                variant="contained"
                endIcon={<ArrowForwardIcon />}
                onClick={continueJourney}
                disabled={
                  currentStepIndex >= totalSteps - 1 &&
                  completedStepIds.includes(activeStep?.id ?? "")
                }
              >
                Continue
              </Button>
              <Button
                size="small"
                color={confirmDismiss ? "error" : "inherit"}
                onClick={handleDismiss}
                onBlur={() => setConfirmDismiss(false)}
              >
                {confirmDismiss ? "Confirm dismiss" : "Dismiss"}
              </Button>
            </Stack>
          </Stack>
        </Container>
      </Paper>

      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        PaperProps={{
          sx: {
            width: { xs: "100%", sm: 420, md: 480, lg: 560 },
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          },
        }}
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ px: 2, pt: 2, pb: 1, flexShrink: 0 }}
        >
          <Typography variant="h6">Guided journey</Typography>
          <IconButton onClick={() => setDrawerOpen(false)} aria-label="Close journey drawer">
            <CloseIcon />
          </IconButton>
        </Stack>
        <Box sx={{ flex: 1, overflow: "auto", minHeight: 0, px: 2, pb: 2 }}>
          <GuidedJourneyPanel
            journey={journey}
            currentStepIndex={currentStepIndex}
            completedStepIds={completedStepIds}
            onStepAction={(stepIndex) => {
              executeStep(stepIndex);
              setDrawerOpen(false);
            }}
          />
        </Box>
      </Drawer>
    </>
  );
};

export default JourneyCompanionBar;
